////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// DOM-UTILS.TS — small, reusable DOM/utility primitives (TS port of includes/js/dom-utils.js; Wave 2c of
// plans/bundler-script-load-order-phase2.md, itself Phase 2f of plans/template-js-carve-up.md).
//
// ONLY the genuinely-cohesive, (near-)pure primitives — fade animations, debounce/input throttling, a
// numeric predicate, and a generic show/hide toggle.
//
// LOAD ORDER (template_page_footer.php): EARLY — after item-arr-readers, before every bundle + template.js
// + the sibling inits (quill-init etc. call these). MODULE-SCOPE SAFE (clean fold): eval-time work = the 6
// window attaches ONLY. They're pure except toggleElement (uses ftDebugLog + the legacy bare `event` global)
// and debounceInput (looks up window[callbackName]) — both only at call time, by which point template.js
// has loaded.
//
// PUBLIC GLOBALS (window-exported): fadeOut, fadeIn, debounce, debounceInput, isNumeric, toggleElement.
// The original plain-JS file's explicit attach list was already COMPLETE (all 6 functions), so this port
// is a 1:1 surface match — no auto-attach trap (unlike core-helpers / item-arr-readers).
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function fadeOut(element: HTMLElement, duration = 400): void {
    element.style.opacity = '1';

    let startTime = performance.now();

    function fade() {
        let elapsedTime = performance.now() - startTime;
        let progress = Math.min(elapsedTime / duration, 1);
        element.style.opacity = String(1 - progress);

        if (progress < 1) {
            requestAnimationFrame(fade);
        } else {
            element.style.display = "none";
        }
    }

    fade();
}

function fadeIn(element: HTMLElement, duration = 400): void {
    element.style.opacity = '0';
    element.style.display = "block";

    let startTime = performance.now();

    function fade() {
        let elapsedTime = performance.now() - startTime;
        let progress = Math.min(elapsedTime / duration, 1);
        element.style.opacity = String(progress);

        if (progress < 1) {
            requestAnimationFrame(fade);
        }
    }

    fade();
}

function debounce<TThis, TArgs extends unknown[]>(func: (this: TThis, ...args: TArgs) => unknown, delay: number): (this: TThis, ...args: TArgs) => void {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    return function (this: TThis, ...args: TArgs): void {
        if (timeout !== undefined) clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
}

// Global debounce function
function debounceInput(element: HTMLInputElement, callbackStr: string): void {
    let callbackName: string;
    let argType: 'event' | 'call' | 'value_element';
    const match = callbackStr.match(/^(\w+)\(event\)\s*;?$/);
    if (match) {
        callbackName = match[1] ?? callbackStr;
        argType = 'event';
    } else {
        const noArgCall = callbackStr.match(/^(\w+)\(\)\s*;?\s*$/);
        if (noArgCall) {
            callbackName = noArgCall[1] ?? callbackStr;
            argType = 'call';
        } else {
            callbackName = callbackStr;
            argType = 'value_element';
        }
    }

    let timeoutId = element.dataset.debounceTimeout;

    if (timeoutId) {
        clearTimeout(Number(timeoutId));
    }

    const nextTimeoutId = setTimeout(() => {
        const fn = (window as unknown as Record<string, unknown>)[callbackName];
        if (typeof fn !== 'function') {
            return;
        }
        if (argType === 'event') {
            const fakeEvent = { target: element };
            fn(fakeEvent);
        } else if (argType === 'call') {
            fn();
        } else {
            fn(element.value, element);
        }
    }, 500);

    element.dataset.debounceTimeout = String(nextTimeoutId);
}

function isNumeric(value: unknown): boolean {
    return !Number.isNaN(Number.parseFloat(String(value))) && Number.isFinite(Number(value));
}

function toggleElement(lookup: string): void {
    let element: HTMLElement | null = null;

    // 1. Try by ID first (just in case)
    if (lookup) {
        element = document.getElementById(lookup);
    }

    // 2. If not found → try by class name (first match)
    if (!element && lookup) {
        const elements = document.getElementsByClassName(lookup);
        if (elements.length > 0) {
            element = elements[0] instanceof HTMLElement ? elements[0] : null; // Use first matching element
        }
    }

    // 3. If still not found → search from the button that was clicked (legacy bare `event` global)
    const ev = (typeof event !== 'undefined') ? event : window.event;
    const eventTarget = ev?.target;
    if (!element && eventTarget && typeof (eventTarget as Element).closest === 'function') {
        const target = eventTarget as Element;
        const clickedBtn = target.closest('button') || target;
        const sibling = clickedBtn.closest('.ft3d-top-right-controls')?.nextElementSibling;
        element = sibling instanceof HTMLElement ? sibling : null;

        // Or more generally: find the panel anywhere after the controls
        if (!element || !element.classList.contains(lookup)) {
            element = document.querySelector<HTMLElement>('.' + lookup);
        }
    }

    // 4. Final fallback: querySelector (works globally)
    if (!element && lookup) {
        element = document.querySelector<HTMLElement>('.' + lookup);
    }

    // 5. If still nothing → warn and exit
    if (!element) {
        ftDebugLog('toggleElement', { warn: 'No element found', lookup });
        return;
    }

    // 6. Toggle display (handles inline + CSS)
    const style = window.getComputedStyle(element);
    const isHidden = element.style.display === 'none' || style.display === 'none';
    element.style.display = isHidden ? 'block' : 'none';
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// WINDOW EXPORTS
declare global {
    interface Window {
        fadeOut: typeof fadeOut;
        fadeIn: typeof fadeIn;
        debounce: typeof debounce;
        debounceInput: typeof debounceInput;
        isNumeric: typeof isNumeric;
        toggleElement: typeof toggleElement;
    }
}

window.fadeOut = fadeOut;
window.fadeIn = fadeIn;
window.debounce = debounce;
window.debounceInput = debounceInput;
window.isNumeric = isNumeric;
window.toggleElement = toggleElement;

export {};
